import { Trans } from '@lingui/react';
import { DialogActions, DialogContent, Dialog, DialogTitle } from '@startuptools/ui/Dialog';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { Controller, useForm } from 'react-hook-form';

export const EditQuantityDialog = ({
  close,
  sharesLeftToDistribute,
  onSave,
  defaultValue,
}: {
  close: () => void;
  sharesLeftToDistribute: number;
  defaultValue?: number;
  onSave: (data: { quantity: string }) => void;
}) => {
  const { control, setValue, handleSubmit } = useForm<{ quantity: string }>({
    defaultValues: { quantity: defaultValue?.toString() },
  });
  const leftIncludingAlreadyDistributed = sharesLeftToDistribute + (defaultValue ?? 0);
  return (
    <Dialog open>
      <DialogTitle>
        <Trans id="Edit quantity" />
      </DialogTitle>
      <form onSubmit={handleSubmit(onSave)}>
        <DialogContent>
          <FormControl>
            <FormLabel>
              <Trans id="Quantity" />
            </FormLabel>
            <Controller
              control={control}
              name="quantity"
              rules={{ required: true, min: 1, max: leftIncludingAlreadyDistributed }}
              render={({ field, formState }) => (
                <FormControl error={Boolean(formState.errors.quantity)}>
                  <NumericFormatInput
                    {...field}
                    endDecorator={
                      <Button
                        variant="soft"
                        color="neutral"
                        onClick={() => {
                          setValue('quantity', leftIncludingAlreadyDistributed.toString());
                        }}
                      >
                        {leftIncludingAlreadyDistributed}
                      </Button>
                    }
                  />
                  {Boolean(formState.errors.quantity) && (
                    <FormHelperText>
                      <InfoOutlinedIcon />
                      {formState.errors.quantity?.type === 'max' && (
                        <Trans
                          id="There's only {quantity} shares left to allocate"
                          values={{ quantity: leftIncludingAlreadyDistributed }}
                        />
                      )}
                      {formState.errors.quantity?.type === 'min' && <Trans id="Allocate at least 1 share" />}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={close}>
            <Trans id="Cancel" />
          </Button>
          <Button type="submit">
            <Trans id="Confirm" />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
